import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Spinner } from '../components/ui/Spinner.js';
import { useMemberPreferences } from '../features/dashboards/api.js';
import { DashboardPage } from './DashboardPage.js';

/**
 * Whether this browser session has already resolved where to land. Module scope on
 * purpose: it resets on a full page load (which is exactly "opening the app") but
 * survives in-app navigation, so clicking Home in the sidebar afterwards actually shows
 * Home instead of bouncing back to the pinned dashboard.
 */
let landingResolved = false;

/**
 * The `/` route. Renders Home, unless the member has pinned a dashboard as their landing
 * page — in which case the first load of the session redirects there.
 */
export function HomeRoute(): JSX.Element {
  const navigate = useNavigate();
  const preferences = useMemberPreferences();
  const pending = !landingResolved && preferences.isLoading;

  useEffect(() => {
    if (landingResolved || preferences.isLoading) return;
    landingResolved = true;
    const pinned = preferences.data?.defaultDashboardId;
    // A pinned dashboard that has since been deleted or hidden simply 404s on its own
    // page, so no extra validation is needed here.
    if (pinned) navigate(`/dashboards/${pinned}`, { replace: true });
  }, [preferences.isLoading, preferences.data, navigate]);

  if (pending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return <DashboardPage />;
}
